import * as Sentry from '@sentry/browser'
import type Pusher from 'pusher-js'
import type { Channel } from 'pusher-js'

import {
  type EventBusDomainEventMap,
  type EventBusTransportEventMap,
  type EventBusConnectionStateEnum,
  type EventBusTransportErrorCodeEnum,
  type EventBusEventKey,
  type IEventBus,
  type EventBusTransportError,
  type EventBusEventMap,
  EventBusTransportEventTypes,
  EventBusTransportErrorCodes,
  EventBusConnectionStates,
} from '@webapp/infrastructure/interfaces/services/IEventBus'
import {
  ChannelVisibility,
  CHANNEL_PREFIX,
  PusherToEventBusState,
} from './constants'
import {
  type ChannelVisibilityEnum,
  type EventHandler,
  type EventHandlerSet,
  type PusherConnectionState,
  type PusherEventBusOptions,
  type PusherRuntimeError,
} from './types'

/**
 * Pusher-based implementation of IEventBus. It wraps a provided Pusher instance
 * and exposes a normalized event-bus interface for domain and transport events.
 * This EventBus instance manages a single channel specified at construction.
 *
 * Transport errors are normalized and reported to Sentry. The 4004 close code
 * (the app is over its Pusher connection limit) is tagged separately, and only
 * once per app session. Pusher tolerates being over the limit before it
 * hard-limits, so 4004 is a late signal worth isolating from routine transport
 * errors.
 */
export class PusherEventBus<
  T extends EventBusDomainEventMap = EventBusDomainEventMap,
> implements IEventBus<T> {
  private static readonly PUSHER_STATE_CHANGE_EVENT = 'state_change'
  private static readonly PUSHER_ERROR_EVENT = 'error'
  private static readonly PUSHER_SUBSCRIPTION_ERROR_EVENT =
    'pusher:subscription_error'

  private static readonly PUSHER_AUTH_ERROR_TYPE = 'AuthError'
  private static readonly PUSHER_WEBSOCKET_ERROR_TYPE = 'WebSocketError'
  private static readonly PUSHER_HANDSHAKE_ERROR_TYPE = 'HandshakeError'
  private static readonly PUSHER_ERROR_TYPE = 'PusherError'

  private static readonly DEFAULT_ERROR_MESSAGE = 'Unknown error'

  private static readonly SENTRY_COMPONENT_TAG = 'component'
  private static readonly SENTRY_COMPONENT_NAME = 'PusherEventBus'
  private static readonly SENTRY_PUSHER_STATE_CONTEXT = 'pusher_state'
  private static readonly SENTRY_PUSHER_TRANSPORT_ERROR_CONTEXT =
    'pusher_transport_error'
  private static readonly SENTRY_CONNECTION_LIMIT_EXCEEDED_TAG =
    'pusher_connection_limit_exceeded'

  private static readonly CONNECTION_LIMIT_EXCEEDED_CODE = 4004

  private static hasCapturedConnectionLimitExceeded = false

  private readonly pusher: Pusher
  private readonly channelName: string
  private readonly visibility?: ChannelVisibilityEnum
  private currentChannel?: Channel

  /**
   * Handler for connection state changes with Pusher-compatible signature.
   */
  private readonly handleConnectionStateChange = (data?: unknown) => {
    if (PusherEventBus.isConnectionStateData(data)) {
      const currentState = data.current

      if (!PusherEventBus.isPusherConnectionState(currentState)) {
        Sentry.withScope((scope) => {
          scope.setTag(
            PusherEventBus.SENTRY_COMPONENT_TAG,
            PusherEventBus.SENTRY_COMPONENT_NAME
          )
          scope.setContext(PusherEventBus.SENTRY_PUSHER_STATE_CONTEXT, {
            received_state: currentState,
          })
          Sentry.captureException(
            new Error(`Unexpected Pusher connection state: ${currentState}`)
          )
        })
      }

      this.emitTransportStateEvent(this.mapConnectionState(currentState))
    }
  }

  /**
   * Handler for connection errors with Pusher-compatible signature.
   */
  private readonly handleConnectionError = (data?: unknown) => {
    this.emitTransportErrorEvent(this.normalizeError(data))
  }

  /**
   * Handler for subscription errors with Pusher-compatible signature.
   */
  private readonly handleSubscriptionError = (data?: unknown) => {
    this.emitTransportErrorEvent(this.normalizeError(data))
  }

  /**
   * Registry of domain event handlers (custom business events).
   */
  private readonly domainEventHandlers = new Map<
    keyof T,
    EventHandlerSet<T[keyof T]>
  >()

  /**
   * Registry of bound connection event names.
   */
  private readonly boundConnectionEvents = new Set<string>()

  /**
   * Registry of STATE transport event handlers.
   */
  private readonly transportStateEventHandlers: Set<
    EventHandler<EventBusTransportEventMap['state']>
  > = new Set()

  /**
   * Registry of ERROR transport event handlers.
   */
  private readonly transportErrorEventHandlers: Set<
    EventHandler<EventBusTransportEventMap['error']>
  > = new Set()

  constructor(pusherInstance: Pusher, options: PusherEventBusOptions) {
    this.pusher = pusherInstance
    this.channelName = options.channelName
    this.visibility = options.visibility
  }

  /**
   * Establishes a connection to the Pusher channel.
   * Idempotent - safe to call multiple times.
   * Handlers registered before connect() are automatically bound upon connection.
   *
   * @example
   * ```typescript
   * eventBus.bind('order-created', (data) => console.log(data))
   * eventBus.connect() // Handler will now receive events
   * ```
   */
  connect(): void {
    // Subscribe to configured channel
    const visibilityPrefixedChannelName =
      this.getVisibilityPrefixedChannelName()

    if (this.currentChannel) {
      return
    }

    this.bindConnectionEventHandlers()

    try {
      // pusher.channel(name) returns the existing channel if already subscribed, otherwise null.
      // pusher.subscribe(name) creates a new subscription if not already subscribed.
      this.currentChannel =
        this.pusher.channel(visibilityPrefixedChannelName) ??
        this.pusher.subscribe(visibilityPrefixedChannelName)

      if (!this.currentChannel) {
        this.emitTransportErrorEvent({
          code: EventBusTransportErrorCodes.CONNECTION_ERROR,
          message: `Failed to subscribe to channel "${visibilityPrefixedChannelName}"`,
        })
        return
      }

      // Bind any previously registered domain handlers now that we are connected
      this.bindAllDomainEventHandlersToChannel()

      // Listen for channel subscription errors
      this.bindSubscriptionErrorEventHandler()
    } catch (error) {
      this.emitTransportErrorEvent({
        code: EventBusTransportErrorCodes.CONNECTION_ERROR,
        message: `Failed to subscribe to channel "${visibilityPrefixedChannelName}": ${
          error instanceof Error ? error.message : String(error)
        }`,
        cause: error,
      })
    }
  }

  /**
   * Closes the connection and releases all resources.
   * Safe to call even if not currently connected.
   *
   * @example
   * ```typescript
   * eventBus.disconnect()
   * ```
   */
  disconnect(): void {
    // Unbind domain handlers first
    this.unbindAllDomainEventHandlers()

    // Unbind channel-level error handler
    this.unbindSubscriptionErrorEventHandler()

    // Unbind connection-level handlers
    this.unbindConnectionEventHandlers()

    // Clear transport handlers last (after all potential error-generating operations)
    this.transportStateEventHandlers.clear()
    this.transportErrorEventHandlers.clear()

    // Prefer channel-level unsubscribe to avoid tearing down all pusher usage
    const visibilityPrefixedChannelName =
      this.getVisibilityPrefixedChannelName()
    this.pusher.unsubscribe(visibilityPrefixedChannelName)
    this.currentChannel = undefined
  }

  /**
   * Registers a callback for a specific event.
   * Supports domain events and transport events (STATE, ERROR).
   * If called before connect(), the handler is automatically bound upon connection.
   *
   * @param event - Event name or transport event type
   * @param callback - Function to invoke when the event occurs
   *
   * @example
   * ```typescript
   * eventBus.bind('order-created', (data) => console.log(data.orderId))
   * eventBus.bind(EventBusTransportEventTypes.STATE, ({ state }) => console.log(state))
   * ```
   */
  bind<K extends EventBusEventKey<T>>(
    event: K,
    callback: (data: EventBusEventMap<T>[K]) => void
  ): void {
    const eventName = String(event)

    if (PusherEventBus.isStatusEventHandler(eventName, callback)) {
      this.bindTransportStateEvent(callback)
      return
    }

    if (PusherEventBus.isErrorEventHandler(eventName, callback)) {
      this.bindTransportErrorEvent(callback)
      return
    }

    // Type assertion: After transport event checks, we know this is a domain event.
    // TypeScript cannot narrow K from EventBusEventKey<T> to keyof T, so we assert the callback
    // type from EventBusEventMap<T>[K] to T[keyof T] (which is safe since K is keyof T here).
    this.bindDomainEvent(event, callback as EventHandler<T[keyof T]>)
  }

  /**
   * Unregisters a specific callback from an event.
   * Safe to call even if the callback was never registered.
   *
   * @param event - Event name or transport event type
   * @param callback - Exact function reference to remove
   *
   * @example
   * ```typescript
   * const handler = (data) => console.log(data)
   * eventBus.bind('order-created', handler)
   * eventBus.unbind('order-created', handler)
   * ```
   */
  unbind<K extends EventBusEventKey<T>>(
    event: K,
    callback: (data: EventBusEventMap<T>[K]) => void
  ): void {
    const eventName = String(event)

    if (PusherEventBus.isStatusEventHandler(eventName, callback)) {
      this.unbindTransportStateEvent(callback)
      return
    }

    if (PusherEventBus.isErrorEventHandler(eventName, callback)) {
      this.unbindTransportErrorEvent(callback)
      return
    }

    // Type assertion: After transport event checks, we know this is a domain event.
    // TypeScript cannot narrow K from EventBusEventKey<T> to keyof T, so we assert the callback
    // type from EventBusEventMap<T>[K] to T[keyof T] (which is safe since K is keyof T here).
    this.unbindDomainEvent(event, callback as EventHandler<T[keyof T]>)
  }

  /**
   * Unregisters all callbacks for a specific event, or all events if omitted.
   *
   * @param event - Event name to unbind. If omitted, unbinds all events.
   *
   * @example
   * ```typescript
   * eventBus.unbindAll('order-created') // Remove all handlers for one event
   * eventBus.unbindAll() // Remove all handlers
   * ```
   */
  unbindAll(event?: EventBusEventKey<T>): void {
    if (!event) {
      // Unbind all domain and transport handlers
      this.unbindAllDomainEventHandlers()
      this.transportStateEventHandlers.clear()
      this.transportErrorEventHandlers.clear()
      return
    }

    const eventName = String(event)

    if (eventName === EventBusTransportEventTypes.STATE) {
      this.transportStateEventHandlers.clear()
      return
    }

    if (eventName === EventBusTransportEventTypes.ERROR) {
      this.transportErrorEventHandlers.clear()
      return
    }

    // Type assertion: After transport event checks, we know this is a domain event.
    // TypeScript cannot narrow EventBusEventKey<T> to keyof T, so we assert (which is safe).
    // Unbind all handlers for specific domain event
    this.unbindDomainEvent(event)
  }

  /**
   * Emits a STATE transport event to registered handlers.
   */
  private emitTransportStateEvent(state: EventBusConnectionStateEnum) {
    const payload = { state }
    for (const handler of this.transportStateEventHandlers) {
      handler(payload)
    }
  }

  /**
   * Emits an ERROR transport event to registered handlers and reports it to Sentry.
   */
  private emitTransportErrorEvent(payload: EventBusTransportError) {
    const shouldTagConnectionLimitExceeded =
      !PusherEventBus.hasCapturedConnectionLimitExceeded &&
      PusherEventBus.isConnectionLimitExceededError(payload.cause)

    if (shouldTagConnectionLimitExceeded) {
      PusherEventBus.hasCapturedConnectionLimitExceeded = true
    }

    Sentry.withScope((scope) => {
      scope.setTag(
        PusherEventBus.SENTRY_COMPONENT_TAG,
        PusherEventBus.SENTRY_COMPONENT_NAME
      )

      if (shouldTagConnectionLimitExceeded) {
        scope.setTag(
          PusherEventBus.SENTRY_CONNECTION_LIMIT_EXCEEDED_TAG,
          'true'
        )
      }

      scope.setContext(PusherEventBus.SENTRY_PUSHER_TRANSPORT_ERROR_CONTEXT, {
        channel_name: this.channelName,
        code: payload.code,
        pusher_close_code: PusherEventBus.getPusherCloseCode(payload.cause),
      })

      Sentry.captureException(
        payload.cause instanceof Error
          ? payload.cause
          : new Error(payload.message ?? PusherEventBus.DEFAULT_ERROR_MESSAGE)
      )
    })

    for (const handler of this.transportErrorEventHandlers) {
      handler(payload)
    }
  }

  /**
   * Binds a transport state event handler.
   */
  private bindTransportStateEvent(
    callback: EventHandler<EventBusTransportEventMap['state']>
  ): void {
    this.transportStateEventHandlers.add(callback)
  }

  /**
   * Binds a transport error event handler.
   */
  private bindTransportErrorEvent(
    callback: EventHandler<EventBusTransportEventMap['error']>
  ): void {
    this.transportErrorEventHandlers.add(callback)
  }

  /**
   * Binds a domain event handler.
   */
  private bindDomainEvent<K extends keyof T>(
    event: K,
    callback: EventHandler<T[K]>
  ): void {
    const eventName = String(event)

    // Get or create handler set for this event
    let handlerSet = this.domainEventHandlers.get(event)
    if (!handlerSet) {
      handlerSet = new Set()
      this.domainEventHandlers.set(event, handlerSet)
    }

    // Type assertion: T[K] is a specific event type, but handlerSet stores T[keyof T] (union type).
    // This is safe because T[K] is a subtype of T[keyof T].
    handlerSet.add(callback as EventHandler<T[keyof T]>)

    // Bind to Pusher channel if already connected
    if (this.currentChannel) {
      this.bindDomainEventHandlerToChannel(eventName, callback)
    }
  }

  /**
   * Unbinds a transport state event handler.
   */
  private unbindTransportStateEvent(
    callback: EventHandler<EventBusTransportEventMap['state']>
  ): void {
    this.transportStateEventHandlers.delete(callback)
  }

  /**
   * Unbinds a transport error event handler.
   */
  private unbindTransportErrorEvent(
    callback: EventHandler<EventBusTransportEventMap['error']>
  ): void {
    this.transportErrorEventHandlers.delete(callback)
  }

  /**
   * Unbinds a domain event handler or all handlers for a domain event.
   */
  private unbindDomainEvent<K extends keyof T>(
    event: K,
    callback?: EventHandler<T[K]>
  ): void {
    const eventName = String(event)
    const channel = this.currentChannel

    // Early return: unbind all handlers for this event
    if (!callback) {
      const handlers = this.domainEventHandlers.get(event)
      if (handlers && channel) {
        for (const handler of handlers) {
          channel.unbind(eventName, handler)
        }
      }
      this.domainEventHandlers.delete(event)
      return
    }

    // Unbind specific callback
    if (channel) {
      channel.unbind(eventName, callback)
    }

    // Remove from internal handler registry
    const handlerSet = this.domainEventHandlers.get(event)
    if (handlerSet) {
      // Type assertion: T[K] is a specific event type, but handlerSet stores T[keyof T] (union type).
      // This is safe because T[K] is a subtype of T[keyof T].
      handlerSet.delete(callback as EventHandler<T[keyof T]>)
      // Clean up empty sets to avoid memory leaks
      if (handlerSet.size === 0) {
        this.domainEventHandlers.delete(event)
      }
    }
  }

  /**
   * Binds connection-level handlers to Pusher's connection object.
   */
  private bindConnectionEventHandlers() {
    if (
      !this.boundConnectionEvents.has(PusherEventBus.PUSHER_STATE_CHANGE_EVENT)
    ) {
      this.pusher.connection.bind(
        PusherEventBus.PUSHER_STATE_CHANGE_EVENT,
        this.handleConnectionStateChange
      )
      this.boundConnectionEvents.add(PusherEventBus.PUSHER_STATE_CHANGE_EVENT)
    }

    if (!this.boundConnectionEvents.has(PusherEventBus.PUSHER_ERROR_EVENT)) {
      this.pusher.connection.bind(
        PusherEventBus.PUSHER_ERROR_EVENT,
        this.handleConnectionError
      )
      this.boundConnectionEvents.add(PusherEventBus.PUSHER_ERROR_EVENT)
    }
  }

  /**
   * Unbinds connection-level handlers from Pusher's connection object.
   */
  private unbindConnectionEventHandlers() {
    if (
      this.boundConnectionEvents.has(PusherEventBus.PUSHER_STATE_CHANGE_EVENT)
    ) {
      this.pusher.connection.unbind(
        PusherEventBus.PUSHER_STATE_CHANGE_EVENT,
        this.handleConnectionStateChange
      )
      this.boundConnectionEvents.delete(
        PusherEventBus.PUSHER_STATE_CHANGE_EVENT
      )
    }

    if (this.boundConnectionEvents.has(PusherEventBus.PUSHER_ERROR_EVENT)) {
      this.pusher.connection.unbind(
        PusherEventBus.PUSHER_ERROR_EVENT,
        this.handleConnectionError
      )
      this.boundConnectionEvents.delete(PusherEventBus.PUSHER_ERROR_EVENT)
    }
  }

  /**
   * Binds subscription error handler to the channel.
   */
  private bindSubscriptionErrorEventHandler() {
    if (this.currentChannel) {
      this.currentChannel.bind(
        PusherEventBus.PUSHER_SUBSCRIPTION_ERROR_EVENT,
        this.handleSubscriptionError
      )
    }
  }

  /**
   * Unbinds subscription error handler from the channel.
   */
  private unbindSubscriptionErrorEventHandler() {
    if (this.currentChannel) {
      this.currentChannel.unbind(
        PusherEventBus.PUSHER_SUBSCRIPTION_ERROR_EVENT,
        this.handleSubscriptionError
      )
    }
  }

  /**
   * Binds a single domain handler to the channel.
   */
  private bindDomainEventHandlerToChannel(eventName: string, handler: unknown) {
    if (this.currentChannel) {
      // Note: Pusher's bind() expects (data?: unknown) => void signature
      this.currentChannel.bind(eventName, handler as (data?: unknown) => void)
    }
  }

  /**
   * Binds all domain handlers to the channel.
   */
  private bindAllDomainEventHandlersToChannel() {
    if (this.currentChannel) {
      const channel = this.currentChannel
      for (const [
        eventName,
        handlerSet,
      ] of this.domainEventHandlers.entries()) {
        // Note: Pusher's bind() accepts any function signature
        for (const handler of handlerSet) {
          channel.bind(eventName as string, handler)
        }
      }
    }
  }

  /**
   * Unbinds all domain handlers from the channel and clears the registry.
   */
  private unbindAllDomainEventHandlers() {
    const channel = this.currentChannel
    if (channel) {
      for (const [
        eventName,
        handlerSet,
      ] of this.domainEventHandlers.entries()) {
        // Note: Pusher's unbind() accepts any function signature
        for (const handler of handlerSet) {
          channel.unbind(eventName as string, handler)
        }
      }
    }
    this.domainEventHandlers.clear()
  }

  /**
   * Applies the appropriate visibility prefix to a channel name.
   * Prevents double-prefixing if already present.
   */
  private getVisibilityPrefixedChannelName(): string {
    switch (this.visibility) {
      case ChannelVisibility.PRIVATE:
        return this.channelName.startsWith(CHANNEL_PREFIX.PRIVATE)
          ? this.channelName
          : `${CHANNEL_PREFIX.PRIVATE}${this.channelName}`
      case ChannelVisibility.PRESENCE:
        return this.channelName.startsWith(CHANNEL_PREFIX.PRESENCE)
          ? this.channelName
          : `${CHANNEL_PREFIX.PRESENCE}${this.channelName}`
      case ChannelVisibility.PRIVATE_ENCRYPTED:
        return this.channelName.startsWith(CHANNEL_PREFIX.PRIVATE_ENCRYPTED)
          ? this.channelName
          : `${CHANNEL_PREFIX.PRIVATE_ENCRYPTED}${this.channelName}`
      case ChannelVisibility.PUBLIC:
      default:
        return this.channelName
    }
  }

  /**
   * Maps Pusher connection states to normalized EventBus states.
   */
  private mapConnectionState(state: string): EventBusConnectionStateEnum {
    if (PusherEventBus.isPusherConnectionState(state)) {
      return PusherToEventBusState[state]
    }
    return EventBusConnectionStates.DISCONNECTED
  }

  /**
   * Normalizes Pusher errors to EventBus error format.
   * Handles error structures from Pusher.js 8.4.0-rc2.
   *
   * @param err - Pusher error object (pusher-js doesn't export error types, validated against runtime behavior)
   */
  private normalizeError(err: unknown): EventBusTransportError {
    // Use type guard to safely handle the error data
    if (!PusherEventBus.isPusherRuntimeError(err)) {
      return {
        code: EventBusTransportErrorCodes.UNKNOWN_ERROR,
        message: PusherEventBus.DEFAULT_ERROR_MESSAGE,
        cause: err,
      }
    }

    const errorData = err

    // Map Pusher error types to EventBus error codes
    let code: EventBusTransportErrorCodeEnum
    switch (errorData.type) {
      case PusherEventBus.PUSHER_AUTH_ERROR_TYPE:
        code = EventBusTransportErrorCodes.AUTH_ERROR
        break
      case PusherEventBus.PUSHER_WEBSOCKET_ERROR_TYPE:
      case PusherEventBus.PUSHER_HANDSHAKE_ERROR_TYPE:
      case PusherEventBus.PUSHER_ERROR_TYPE:
        code = EventBusTransportErrorCodes.CONNECTION_ERROR
        break
      default:
        code = EventBusTransportErrorCodes.UNKNOWN_ERROR
    }

    // Extract the most relevant error message from possible Pusher error structures
    const message = PusherEventBus.extractErrorMessage(errorData)

    return { code, message, cause: err }
  }

  /**
   * Extracts the most relevant error message from Pusher error structures.
   * Follows priority order: data.message > nested error.data.message > error
   * (string/Error) > message > fallback. The nested case covers the
   * handshake-refusal shape, where the real message sits under error.data.
   */
  private static extractErrorMessage(errorData: PusherRuntimeError): string {
    return (
      errorData.data?.message ??
      PusherEventBus.resolveErrorFieldMessage(errorData.error) ??
      errorData.message ??
      PusherEventBus.DEFAULT_ERROR_MESSAGE
    )
  }

  /**
   * Resolves a message from the `error` field, which pusher-js populates as a
   * string, an Error, or a nested Pusher error (the handshake-refusal shape,
   * where the message sits under error.data).
   */
  private static resolveErrorFieldMessage(
    error: PusherRuntimeError['error']
  ): string | undefined {
    if (PusherEventBus.isPusherRuntimeError(error)) {
      return error.data?.message ?? error.message
    }

    if (typeof error === 'string') {
      return error
    }

    return error?.message
  }

  /**
   * Type guard to check if data matches Pusher connection state change format
   */
  private static isConnectionStateData(
    data: unknown
  ): data is { previous: string; current: string } {
    return (
      data !== null &&
      typeof data === 'object' &&
      'previous' in data &&
      'current' in data &&
      typeof (data as any).previous === 'string' &&
      typeof (data as any).current === 'string'
    )
  }

  /**
   * Type guard to check if a string is a valid PusherConnectionState
   */
  private static isPusherConnectionState(
    state: string
  ): state is PusherConnectionState {
    return state in PusherToEventBusState
  }

  /**
   * Type guard to check if data matches PusherRuntimeError format
   */
  private static isPusherRuntimeError(
    data: unknown
  ): data is PusherRuntimeError {
    return (
      data !== null &&
      typeof data === 'object' &&
      (('type' in data && typeof (data as any).type === 'string') ||
        'error' in data ||
        ('message' in data && typeof (data as any).message === 'string') ||
        ('data' in data && typeof (data as any).data === 'object'))
    )
  }

  /**
   * Detects Pusher's 4004 (connection limit exceeded) close code. pusher-js
   * surfaces it in two shapes on the connection `error` event: directly
   * ({ data: { code } }) when an established connection closes, and wrapped one
   * level deeper ({ error: { data: { code } } }) when the handshake is refused —
   * the usual path for an over-limit connection, since Pusher refuses it before
   * the handshake completes.
   */
  private static isConnectionLimitExceededError(cause: unknown): boolean {
    if (!PusherEventBus.isPusherRuntimeError(cause)) {
      return false
    }

    return (
      cause.data?.code === PusherEventBus.CONNECTION_LIMIT_EXCEEDED_CODE ||
      (PusherEventBus.isPusherRuntimeError(cause.error) &&
        cause.error.data?.code ===
          PusherEventBus.CONNECTION_LIMIT_EXCEEDED_CODE)
    )
  }

  /**
   * Extracts Pusher's raw close code from either error shape (direct
   * data.code or handshake-wrapped error.data.code), for Sentry context.
   */
  private static getPusherCloseCode(cause: unknown): number | undefined {
    if (!PusherEventBus.isPusherRuntimeError(cause)) {
      return undefined
    }

    return (
      cause.data?.code ??
      (PusherEventBus.isPusherRuntimeError(cause.error)
        ? cause.error.data?.code
        : undefined)
    )
  }

  /**
   * Type guard to check if callback is for state transport events
   */
  private static isStatusEventHandler(
    eventName: string,
    callback: unknown
  ): callback is EventHandler<EventBusTransportEventMap['state']> {
    return (
      eventName === EventBusTransportEventTypes.STATE &&
      typeof callback === 'function'
    )
  }

  /**
   * Type guard to check if callback is for error transport events
   */
  private static isErrorEventHandler(
    eventName: string,
    callback: unknown
  ): callback is EventHandler<EventBusTransportEventMap['error']> {
    return (
      eventName === EventBusTransportEventTypes.ERROR &&
      typeof callback === 'function'
    )
  }
}
